export type Tier = "free" | "pro" | "agency";

/* ── Quota limits ──────────────────────────────────────────────── */

/**
 * Monthly check quota per tier.
 *
 * IMPORTANT: the same numbers are hard-coded in
 * `supabase/migrations/027_update_check_limits.sql` inside the
 * `claim_check_slot` and `get_user_quota` RPCs. The drift-guard test in
 * `src/__tests__/lib/tier-limits-sql-parity.test.ts` fails the build if
 * those fall out of sync — update both, or add a new migration.
 */
export const TIER_LIMITS: Record<Tier, number> = {
  free: 1,
  pro: 10,
  agency: 999_999,
};

/* ── Followed places limits ──────────────────────────────────── */

export const FOLLOWED_PLACES_LIMITS: Record<Tier, number> = {
  free: 1,
  pro: 3,
  agency: 999_999,
};

/* ── Re-check cooldown (milliseconds) ──────────────────────────── */

export const RECHECK_COOLDOWN_MS: Record<Tier, number> = {
  free: 90 * 24 * 60 * 60 * 1000,   // 90 days
  pro: 7 * 24 * 60 * 60 * 1000,     // 7 days (weekly)
  agency: 0,                          // no cooldown
};

/* ── Engine visibility per tier ────────────────────────────────── */

/** Engine labels visible to each tier. Free sees only ChatGPT + Perplexity. */
export const TIER_VISIBLE_ENGINES: Record<Tier, string[] | "all"> = {
  free: ["openai", "perplexity"],
  pro: "all",
  agency: "all",
};

/* ── Rate limiting ─────────────────────────────────────────────── */

export const RATE_LIMITS: Record<Tier, { checksPerMinute: number }> = {
  free: { checksPerMinute: 1 },
  pro: { checksPerMinute: 5 },
  agency: { checksPerMinute: 20 },
};

/* ── Global cache TTL (milliseconds) ──────────────────────────── */

/**
 * Ceiling on how old a pending/processing check can be before we stop
 * treating it as "in-flight" and let the user start a new one. Unrelated
 * to the cache-share window below.
 */
export const GLOBAL_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Free users always receive the global cache when available and never trigger a fresh analysis. */
export const FREE_ALWAYS_CACHE = true;

/**
 * Per-tier window where a completed check by any user can be re-served
 * to this user as a cache-copy (consumes one of their attempts, but does
 * not re-run engines). Outside this window, a fresh check is triggered.
 *   - Free pays nothing → tolerate stale data for longer (30 days).
 *   - Pro / Agency want fresher data → 7 days.
 */
const CACHE_SHARE_WINDOW_MS: Record<Tier, number> = {
  free: 30 * 24 * 60 * 60 * 1000,
  pro: 7 * 24 * 60 * 60 * 1000,
  agency: 7 * 24 * 60 * 60 * 1000,
};

export function getCacheShareWindowMs(tier: Tier): number {
  return CACHE_SHARE_WINDOW_MS[tier];
}

/* ── Feature gating ────────────────────────────────────────────── */

export interface TierFeatureGate {
  /** Full synthesis report (cross-engine analysis) */
  synthesis: boolean;
  /** Full shadow profile (all fields vs. basic how_ai_sees_you only) */
  fullShadowProfile: boolean;
  /** What-if score predictions */
  whatIfAnalysis: boolean;
  /** Copy-paste marketer brief */
  marketerBrief: boolean;
  /** Paste-ready blocks for Google Business, website head, and social bios */
  pasteBlocks: boolean;
  /** Voice search simulation */
  voiceSearch: boolean;
  /** Tourist vs local audience breakdown */
  audienceBreakdown: boolean;
  /** Full action plan (free only sees "do today") */
  fullActionPlan: boolean;
  /** Full competitor list (free only sees top 3 citywide) */
  fullCompetitors: boolean;
  /** PDF report download */
  pdfExport: boolean;
  /** Score history and dynamics tracking */
  scoreTracking: boolean;
  /** Can force a fresh analysis even if cache exists */
  canForceRefresh: boolean;
  /** Rekkd Insights — seasonal/event-based reports */
  rekkdInsights: boolean;
  /** Competitor benchmark dashboard */
  competitorBenchmark: boolean;
  /** Max competitors per place for benchmark */
  maxCompetitorsPerPlace: number;
  /** Place activity feed (tasks + notes) */
  activityFeed: boolean;
  /** In-app + push notifications for followed places */
  notifications: boolean;
  /** Review sentiment over time chart (Outscraper-backed) */
  reviewSentimentChart: boolean;
  /** Review Digest tab (SWOT-style summary + AI words) */
  reviewDigest: boolean;
  /** Photo health card — owner vs user photo mix, bucket band */
  photoHealth: boolean;
  /** Menu AI-readability card — format tier, score, blockers */
  menuReadability: boolean;
}

export const TIER_FEATURES: Record<Tier, TierFeatureGate> = {
  free: {
    synthesis: false,
    fullShadowProfile: false,
    whatIfAnalysis: false,
    marketerBrief: false,
    pasteBlocks: false,
    voiceSearch: false,
    audienceBreakdown: false,
    fullActionPlan: false,
    fullCompetitors: false,
    pdfExport: false,
    scoreTracking: false,
    canForceRefresh: false,
    rekkdInsights: false,
    competitorBenchmark: false,
    maxCompetitorsPerPlace: 0,
    activityFeed: false,
    notifications: false,
    reviewSentimentChart: false,
    reviewDigest: false,
    photoHealth: false,
    menuReadability: false,
  },
  pro: {
    synthesis: true,
    fullShadowProfile: true,
    whatIfAnalysis: true,
    marketerBrief: true,
    pasteBlocks: true,
    voiceSearch: true,
    audienceBreakdown: true,
    fullActionPlan: true,
    fullCompetitors: true,
    pdfExport: true,
    scoreTracking: true,
    canForceRefresh: true,
    rekkdInsights: true,
    competitorBenchmark: true,
    maxCompetitorsPerPlace: 5,
    activityFeed: true,
    notifications: true,
    reviewSentimentChart: true,
    reviewDigest: true,
    photoHealth: true,
    menuReadability: true,
  },
  agency: {
    synthesis: true,
    fullShadowProfile: true,
    whatIfAnalysis: true,
    marketerBrief: true,
    pasteBlocks: true,
    voiceSearch: true,
    audienceBreakdown: true,
    fullActionPlan: true,
    fullCompetitors: true,
    pdfExport: true,
    scoreTracking: true,
    canForceRefresh: true,
    rekkdInsights: true,
    competitorBenchmark: true,
    maxCompetitorsPerPlace: 10,
    activityFeed: true,
    notifications: true,
    reviewSentimentChart: true,
    reviewDigest: true,
    photoHealth: true,
    menuReadability: true,
  },
};

/* ── Pricing page config ───────────────────────────────────────── */

/** Threshold above which a quota is considered unlimited for display purposes. */
const UNLIMITED_QUOTA_THRESHOLD = 10_000;

function formatChecksPerMonth(tier: Tier): string {
  const limit = TIER_LIMITS[tier];
  if (limit >= UNLIMITED_QUOTA_THRESHOLD) return "Unlimited checks";
  return `${limit} check${limit === 1 ? "" : "s"}/month`;
}

function formatFollowLimit(tier: Tier): string {
  const limit = FOLLOWED_PLACES_LIMITS[tier];
  if (limit >= UNLIMITED_QUOTA_THRESHOLD) return "Unlimited followed places";
  if (limit === 1) return "Follow 1 place";
  return `Follow up to ${limit} places`;
}

export const TIER_CONFIG: Record<Tier, {
  label: string;
  price: string;
  period: string;
  checksPerMonth: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted: boolean;
}> = {
  free: {
    label: "Free",
    price: "$0",
    period: "forever",
    checksPerMonth: formatChecksPerMonth("free"),
    features: [
      "AI visibility score & grade",
      "2 AI engines (ChatGPT + Perplexity)",
      "AI quotes from 2 engines",
      "Basic scenario map",
      "Top 3 competitors (citywide)",
      "\"Do today\" action items",
      formatFollowLimit("free"),
    ],
    cta: "Get Started",
    ctaLink: "/auth/signup",
    highlighted: false,
  },
  pro: {
    label: "Pro",
    price: "$19",
    period: "/month",
    checksPerMonth: formatChecksPerMonth("pro"),
    features: [
      "Everything in Free",
      "All 4 AI engines + synthesis report",
      "Full shadow profile — how AI really sees you",
      "What-if analysis & score predictions",
      "Full action plan (today + week + month)",
      "Marketer brief (copy-paste ready)",
      "Competitor deep-dive (citywide + nearby)",
      "Voice search analysis",
      "Tourist vs local lens",
      "Score tracking & weekly dynamics",
      "Rekkd Insights (monthly event reports)",
      "PDF report download",
      formatFollowLimit("pro"),
    ],
    cta: "Start Pro Trial",
    ctaLink: "/auth/signup?plan=pro",
    highlighted: true,
  },
  agency: {
    label: "Agency",
    price: "Custom",
    period: "",
    checksPerMonth: formatChecksPerMonth("agency"),
    features: [
      "Everything in Pro",
      formatFollowLimit("agency"),
      "API access",
      "White-label PDF export",
      "Multi-location dashboard",
      "Bulk competitor analysis",
      "Weekly Rekkd Insights (custom angles)",
      "Dedicated support",
    ],
    cta: "Contact Us",
    ctaLink: "mailto:hello@rekkd.io",
    highlighted: false,
  },
};

/* ── Helpers ───────────────────────────────────────────────────── */

/** Tiers that pay (i.e. anything other than "free"). Use for DB `.in(...)` filters. */
export const PAID_TIERS: readonly Tier[] = ["pro", "agency"] as const;

/** True for any paying tier. Prefer this over `tier === "pro" || tier === "agency"`. */
export function isPaidTier(tier: Tier): boolean {
  return tier !== "free";
}

/** True if a user on this tier can still upgrade. (Agency is the top tier.) */
export function canUpgrade(tier: Tier): boolean {
  return tier !== "agency";
}

/** Boolean feature flag lookup against `TIER_FEATURES`. */
export function hasFeature(
  tier: Tier,
  feature: {
    [K in keyof TierFeatureGate]: TierFeatureGate[K] extends boolean ? K : never;
  }[keyof TierFeatureGate],
): boolean {
  return TIER_FEATURES[tier][feature];
}

/** Check whether a given engine label is visible for a tier. */
export function isEngineVisible(tier: Tier, engineProvider: string): boolean {
  const visible = TIER_VISIBLE_ENGINES[tier];
  if (visible === "all") return true;
  return visible.includes(engineProvider);
}

/** Check if a re-check is allowed based on the last check timestamp. */
export function canRecheck(tier: Tier, lastCheckedAt: Date | null, isAdmin = false): boolean {
  if (isAdmin) return true;
  if (!lastCheckedAt) return true;
  const cooldown = RECHECK_COOLDOWN_MS[tier];
  if (cooldown === 0) return true;
  return Date.now() - lastCheckedAt.getTime() >= cooldown;
}

/** Return the remaining cooldown in human-readable form, or null if re-check is allowed. */
export function recheckCooldownRemaining(tier: Tier, lastCheckedAt: Date | null, isAdmin = false): string | null {
  if (isAdmin) return null;
  if (!lastCheckedAt) return null;
  const cooldown = RECHECK_COOLDOWN_MS[tier];
  if (cooldown === 0) return null;
  const elapsed = Date.now() - lastCheckedAt.getTime();
  if (elapsed >= cooldown) return null;

  const remaining = cooldown - elapsed;
  const days = Math.ceil(remaining / (24 * 60 * 60 * 1000));
  if (days > 1) return `${days} days`;
  const hours = Math.ceil(remaining / (60 * 60 * 1000));
  return `${hours} hours`;
}

/** Return the ISO date when re-check becomes available, or null if already available. */
export function recheckAvailableDate(tier: Tier, lastCheckedAt: Date | null, isAdmin = false): string | null {
  if (isAdmin) return null;
  if (!lastCheckedAt) return null;
  const cooldown = RECHECK_COOLDOWN_MS[tier];
  if (cooldown === 0) return null;
  const availableAt = new Date(lastCheckedAt.getTime() + cooldown);
  if (availableAt.getTime() <= Date.now()) return null;
  return availableAt.toISOString();
}
